import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Rothko from "web/script/modules/rothko";
import _escape from "lodash/escape";
import { gettext } from "web/script/modules/django-i18n";
import { getHtmlProps } from "web/script/modules/html-tag-props";
import { ShippingOptionPropType } from "web/types/buy-option";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import LeadLink from "web/react/components/lead-link/lead-link";
import style from "./shipping.css";

function Shipping({
    className,
    expressShipping,
    freeReturn,
    freeShipping,
    freeShippingUrl,
    leadUrl,
    retailerName,
    linkId,
}) {
    const seeMoreOnSpanProps = {
        class: style.seeMoreOn,
    };
    const seeMoreOnRetailerSpanProps = {
        class: style.link,
    };
    // we escape retailerName, and so mark this string as available for dangerouslySetInnerHTML
    const seeMoreOnRetailerSafeHtml = {
        __html: gettext("in_stock_product.see_more_on_retailer.in_spans", {
            see_more_span_props: getHtmlProps(seeMoreOnSpanProps),
            retailer_name_span_props: getHtmlProps(seeMoreOnRetailerSpanProps),
            retailer_name: _escape(retailerName),
        }),
    };
    const [extraShipping, setExtraShipping] = useState({ data: {} });

    useEffect(() => {
        if (linkId) {
            const options = {
                link_id: linkId,
            };

            Rothko.fetch("modules/overlays/shipping_and_returns_overlay", options)
                .then((res) => setExtraShipping(res))
                .catch(() => {});
        }
    });

    return (
        <div className={classnames(className, style.wrapper)}>
            {freeShipping && (
                <div className={style.freeShipping}>
                    <div className={style.icon}>
                        <SVGIcon name="truck" />
                    </div>
                    {freeShippingUrl ? (
                        <a
                            className={style.shippingLabel}
                            href={freeShippingUrl}
                            dangerouslySetInnerHTML={{ __html: freeShipping.html_message }}
                        />
                    ) : (
                        <span
                            className={style.shippingLabel}
                            dangerouslySetInnerHTML={{ __html: freeShipping.html_message }}
                        />
                    )}
                </div>
            )}
            {expressShipping && (
                <div className={style.expressShipping}>
                    <div className={style.icon}>
                        <SVGIcon name="stopwatch" />
                    </div>
                    <span className={style.shippingLabel}>
                        {gettext("in_stock_product.express_shipping_label")}
                    </span>
                </div>
            )}
            {freeReturn && (
                <div className={style.freeReturn}>
                    <div className={style.icon}>
                        <SVGIcon name="tick-new" />
                    </div>
                    <span className={style.shippingLabel}>
                        {gettext("in_stock_product.free_returns_label")}
                    </span>
                </div>
            )}
            {extraShipping.shipping_options && (
                <section>
                    <p>
                        {gettext("in_stock_product.shipping_by_retailer", {
                            retailer_name: extraShipping.retailer_name,
                        })}
                    </p>
                    {extraShipping.shipping_options.map((option, i) => {
                        return (
                            <div key={i} className={style.option}>
                                <div className={style.optionPrice}>
                                    <em>{option.price}</em>
                                </div>
                                <div>
                                    <div>
                                        <em>{option.name}</em>
                                    </div>
                                    <div className="grey-text">
                                        {option.estimated_delivery}
                                        {option.service && <>{option.service}</>}
                                    </div>
                                    {option.conditions && (
                                        <div>
                                            <span className="glyph glyph-small glyph-tag-red"></span>
                                            <span>{option.conditions}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}
            {extraShipping.returns_method && (
                <section>
                    <h2>{"Returns Policy"}</h2>
                    <p className="mt10">
                        {gettext("in_stock_product.return_by_retailer", {
                            retailer_name: extraShipping.retailer_name,
                        })}
                    </p>
                    <p className="mb5">
                        {"Return Period: "} <em>{extraShipping.returns_period}</em>
                    </p>
                    <p className="mb5 mt5">
                        {"Cost: "} <em>{extraShipping.returns_price}</em>
                    </p>
                    <p className="mb5 mt5">
                        {"Method: "} <em>{extraShipping.returns_method}</em>
                    </p>
                </section>
            )}
            <div className={style.linkHolder}>
                <LeadLink reason="retailer-link" href={leadUrl}>
                    <span dangerouslySetInnerHTML={seeMoreOnRetailerSafeHtml} />
                </LeadLink>
                <SVGIcon name="round-chevron" />
            </div>
        </div>
    );
}

export default Shipping;

Shipping.propTypes = {
    className: PropTypes.string,
    expressShipping: ShippingOptionPropType,
    freeReturn: PropTypes.bool,
    freeShipping: ShippingOptionPropType,
    freeShippingUrl: PropTypes.string,
    leadUrl: PropTypes.string,
    retailerName: PropTypes.string,
    linkId: PropTypes.string,
};

Shipping.defaultProps = {
    expressShipping: null,
    freeReturn: false,
    freeShipping: null,
};
